import { useState, useCallback, useEffect } from 'react';
import Actions from '../store/Actions';
import { showSnackbar } from '../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../context/Snackbar/Types';
import { Group } from '../components/Groups/types';
import { GroupPageType } from '../types/generatedTypes';

interface UseGroupsProps {
  pageType: GroupPageType;
}

export const useGroups = ({ pageType }: UseGroupsProps) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [isGroupingEnabled, setIsGroupingEnabled] = useState(false);
  const [isGroupsModalOpen, setIsGroupsModalOpen] = useState(false);

  // Load groups from backend when needed
  const loadGroupsFromBackend = useCallback(async () => {
    try {
      const groupItems = await Actions.getGroupsForPage(pageType);
      // Convert GroupItem[] to Group[] format expected by the component
      const groups = groupItems.map(item => ({
        id: item.id,
        name: item.name,
        rowIds: item.rowIds,
      }));
      setGroups(groups);
      // Don't auto-enable grouping - let user or view control it
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  }, [pageType]);

  // Auto-load groups on mount (will be controlled by view management later)
  useEffect(() => {
    loadGroupsFromBackend();
  }, [loadGroupsFromBackend]);

  // Groups modal handlers
  const handleOpenGroupsModal = useCallback(() => {
    setIsGroupsModalOpen(true);
  }, []);

  const handleCloseGroupsModal = useCallback(() => {
    setIsGroupsModalOpen(false);
  }, []);

  const handleSaveGroups = useCallback(
    async (newGroups: Group[]) => {
      try {
        // Convert Group[] to GroupItem[] format expected by the API
        const groupItems = newGroups.map(group => ({
          id: group.id,
          name: group.name,
          rowIds: group.rowIds,
          color: undefined,
          description: undefined,
        }));

        // Save to backend
        await Actions.saveGroupsForPage(pageType, groupItems);

        // Update local state
        setGroups(newGroups);
        // Don't auto-enable grouping - let view or user toggle control it
        // Users should explicitly enable grouping via the toggle, not by saving groups
      } catch (error) {
        console.error('Failed to save groups:', error);
        showSnackbar({
          title: 'Error',
          message: 'Failed to save groups',
          type: SnackbarType.error,
        });
      }
    },
    [pageType]
  );

  return {
    // State
    groups,
    setGroups,
    isGroupingEnabled,
    setIsGroupingEnabled,
    isGroupsModalOpen,

    // Functions
    loadGroupsFromBackend,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  };
};
